import React, { useState, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Upload, Download, FileSpreadsheet, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { api } from '../../services/api';
import { useToast } from '../../components/ui/use-toast';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';

const BulkMobileUserPage = () => {
  const { user } = useAuthStore();
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadResults, setUploadResults] = useState(null);

  // Fetch pending approval count
  const { data: pendingData, refetch: refetchPending } = useQuery({
    queryKey: ['pending-approval-count'],
    queryFn: async () => {
      try {
        const response = await api.get('/users/pending-approval');
        return response.data.data;
      } catch (error) {
        console.error('Pending approval fetch error:', error);
        // Return empty data on error instead of throwing
        return { count: 0 };
      }
    },
    enabled: user?.role === 'external_institution',
    refetchInterval: 30000, // Refetch every 30 seconds
    retry: false // Don't retry on error
  });

  // Download template
  const downloadTemplate = async () => {
    try {
      console.log('Download template - User role:', user?.role);
      
      const response = await api.get('/users/export-template', {
        responseType: 'blob',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        },
        validateStatus: (status) => status < 500 // Don't throw on 4xx, we'll handle it
      });

      // Check response status
      if (response.status === 403 || response.status === 401) {
        // Try to parse error message from blob
        let errorMessage = 'Erişim reddedildi';
        if (response.data instanceof Blob) {
          try {
            const text = await response.data.text();
            if (text) {
              const errorData = JSON.parse(text);
              errorMessage = errorData.message || errorData.error || errorMessage;
            }
          } catch (e) {
            console.error('Error parsing blob response:', e);
          }
        }
        console.error('Download template access denied:', {
          status: response.status,
          userRole: user?.role,
          errorMessage
        });
        throw new Error(errorMessage);
      }

      // Check if response is actually an error (JSON blob)
      if (response.data && response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.message || 'Şablon indirilirken hata oluştu');
      }

      // Handle both blob and array buffer responses
      let blob;
      if (response.data instanceof Blob) {
        blob = response.data;
      } else if (response.data instanceof ArrayBuffer) {
        blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
      } else {
        blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'mobil-kullanici-sablonu.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Başarılı',
        description: 'Excel şablonu indirildi',
      });
    } catch (error) {
      console.error('Download template error:', error);
      
      // Try to parse error if it's a blob
      let errorMessage = 'Şablon indirilirken hata oluştu';
      if (error.response?.data instanceof Blob && error.response.data.type === 'application/json') {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // Ignore parse errors
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'Hata',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  // Upload and process Excel file
  const uploadMutation = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/users/bulk-mobile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    },
    onSuccess: (data) => {
      setUploadResults(data.data);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      refetchPending();
      toast({
        title: 'Başarılı',
        description: `${data.data.created} kullanıcı başarıyla eklendi`,
      });
    },
    onError: (error) => {
      console.error('Upload error:', error);
      toast({
        title: 'Hata',
        description: error.response?.data?.message || 'Dosya yüklenirken hata oluştu',
        variant: 'destructive',
      });
    },
  });

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast({
          title: 'Hata',
          description: 'Lütfen Excel dosyası seçin (.xlsx veya .xls)',
          variant: 'destructive',
        });
        return;
      }
      setSelectedFile(file);
      setUploadResults(null);
    }
  };

  const handleUpload = () => {
    if (!selectedFile) {
      toast({
        title: 'Hata',
        description: 'Lütfen bir dosya seçin',
        variant: 'destructive',
      });
      return;
    }

    uploadMutation.mutate(selectedFile);
  };

  const pendingCount = pendingData?.count || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
            Toplu Mobil Kullanıcı Ekleme
          </h1>
          <p className="text-slate-600 mt-2">Excel dosyası ile toplu olarak mobil kullanıcı ekleyin</p>
        </div>

        {/* Pending Approval Badge */}
        {pendingCount > 0 && (
          <Card className="mb-6 border-orange-200 bg-orange-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <AlertCircle className="h-5 w-5 text-orange-600" />
                  <div>
                    <p className="font-semibold text-orange-900">
                      {pendingCount} kullanıcı onay bekliyor
                    </p>
                    <p className="text-sm text-orange-700">
                      Eklediğiniz kullanıcılar admin onayından sonra aktif olacaktır
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Instructions Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Nasıl Kullanılır?</CardTitle>
            <CardDescription>
              Excel şablonunu indirip doldurarak toplu kullanıcı ekleyebilirsiniz
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold text-sm">
                  1
                </div>
                <div>
                  <p className="font-medium">Excel şablonunu indirin</p>
                  <p className="text-sm text-gray-600">Aşağıdaki "Şablon İndir" butonuna tıklayın</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold text-sm">
                  2
                </div>
                <div>
                  <p className="font-medium">Kullanıcı bilgilerini doldurun</p>
                  <p className="text-sm text-gray-600">
                    Ad, Soyad, Telefon (zorunlu, 10 haneli: 5551234567), TC Kimlik No ve Email (opsiyonel) bilgilerini girin. Sistem otomatik olarak telefon numarasına +90 ekleyecektir.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold text-sm">
                  3
                </div>
                <div>
                  <p className="font-medium">Dosyayı yükleyin</p>
                  <p className="text-sm text-gray-600">
                    Doldurduğunuz Excel dosyasını seçip "Yükle" butonuna tıklayın
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold text-sm">
                  4
                </div>
                <div>
                  <p className="font-medium">Admin onayı bekleyin</p>
                  <p className="text-sm text-gray-600">
                    Eklediğiniz kullanıcılar admin onayından sonra aktif olacaktır
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upload Card */}
        <Card>
          <CardHeader>
            <CardTitle>Excel Dosyası Yükle</CardTitle>
            <CardDescription>
              Excel şablonunu doldurup buraya yükleyin
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Download Template Button */}
            <div>
              <Button
                onClick={downloadTemplate}
                variant="outline"
                className="w-full"
                disabled={user?.role !== 'external_institution'}
              >
                <Download className="h-4 w-4 mr-2" />
                Excel Şablonunu İndir
              </Button>
              {user?.role !== 'external_institution' && (
                <p className="text-sm text-red-600 mt-2">
                  Bu işlem için dış kurum yetkisi gereklidir.
                </p>
              )}
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label htmlFor="file-upload">Excel Dosyası Seç</Label>
              <Input
                id="file-upload"
                type="file"
                accept=".xlsx,.xls"
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="cursor-pointer"
              />
              {selectedFile && (
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <FileSpreadsheet className="h-4 w-4" />
                  <span>{selectedFile.name}</span>
                  <Badge variant="outline">{(selectedFile.size / 1024).toFixed(2)} KB</Badge>
                </div>
              )}
            </div>

            {/* Upload Button */}
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || uploadMutation.isPending}
              className="w-full"
            >
              {uploadMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Yükleniyor...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Yükle ve İşle
                </>
              )}
            </Button>

            {/* Upload Results */}
            {uploadResults && (
              <div className="mt-6 space-y-4">
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="font-semibold text-green-900">
                      {uploadResults.created} kullanıcı başarıyla eklendi
                    </span>
                  </div>
                </div>

                {uploadResults.errors > 0 && (
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <XCircle className="h-5 w-5 text-red-600" />
                      <span className="font-semibold text-red-900">
                        {uploadResults.errors} hata oluştu
                      </span>
                    </div>
                    {uploadResults.details?.errors && uploadResults.details.errors.length > 0 && (
                      <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                        {uploadResults.details.errors.map((error, index) => (
                          <div key={index} className="text-sm text-red-700">
                            Satır {error.row}: {error.message}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {uploadResults.details?.success && uploadResults.details.success.length > 0 && (
                  <div className="mt-4">
                    <p className="font-semibold mb-2">Başarıyla eklenen kullanıcılar:</p>
                    <div className="space-y-1 max-h-60 overflow-y-auto">
                      {uploadResults.details.success.map((item, index) => (
                        <div key={index} className="text-sm p-2 bg-gray-50 rounded">
                          Satır {item.row}: {item.user.first_name} {item.user.last_name} ({item.user.email})
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BulkMobileUserPage;

